"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ProtonDrivePublicLinkClient = void 0;
const cache_1 = require("./cache");
const config_1 = require("./config");
const crypto_1 = require("./crypto");
const telemetry_1 = require("./telemetry");
const transformers_1 = require("./transformers");
const apiService_1 = require("./internal/apiService");
const download_1 = require("./internal/download");
const sdkEvents_1 = require("./internal/sdkEvents");
const sharingPublic_1 = require("./internal/sharingPublic");
/**
 * ProtonDrivePublicLinkClient is the interface for the public link client.
 *
 * The client provides high-level operations for managing nodes, and
 * downloading/uploading files.
 *
 * Do not use this client direclty, use ProtonDriveClient instead.
 * The main client handles public link sessions and provides access to
 * public links.
 *
 * See `experimental.getPublicLinkInfo` and `experimental.authPublicLink`
 * for more information.
 */
class ProtonDrivePublicLinkClient {
    logger;
    sdkEvents;
    sharingPublic;
    download;
    experimental;
    constructor({ httpClient, account, openPGPCryptoModule, srpModule, config, telemetry, url, token, publicShareKey, publicRootNodeUid, }) {
        if (!telemetry) {
            telemetry = new telemetry_1.Telemetry();
        }
        this.logger = telemetry.getLogger('interface');
        // Use only in memory cache for public link as there are no events to keep it up to date if persisted.
        const entitiesCache = new cache_1.MemoryCache();
        const cryptoCache = new cache_1.MemoryCache();
        const fullConfig = (0, config_1.getConfig)(config);
        this.sdkEvents = new sdkEvents_1.SDKEvents(telemetry);
        const apiService = new apiService_1.DriveAPIService(telemetry, this.sdkEvents, httpClient, fullConfig.baseUrl, fullConfig.language);
        const cryptoModule = new crypto_1.DriveCrypto(openPGPCryptoModule, srpModule);
        this.sharingPublic = (0, sharingPublic_1.initSharingPublicModule)(telemetry, apiService, entitiesCache, cryptoCache, cryptoModule, account, url, token, publicShareKey, publicRootNodeUid);
        this.download = (0, download_1.initDownloadModule)(telemetry, apiService, cryptoModule, account, this.sharingPublic.shares, this.sharingPublic.nodes.access, this.sharingPublic.nodes.revisions);
        this.experimental = {
            getNodeUrl: async (nodeUid) => {
                this.logger.debug(`Getting node URL for ${(0, transformers_1.getUid)(nodeUid)}`);
                return this.sharingPublic.nodes.access.getNodeUrl((0, transformers_1.getUid)(nodeUid));
            },
            getDocsKey: async (nodeUid) => {
                this.logger.debug(`Getting docs keys for ${(0, transformers_1.getUid)(nodeUid)}`);
                const keys = await this.sharingPublic.nodes.access.getNodeKeys((0, transformers_1.getUid)(nodeUid));
                if (!keys.contentKeyPacketSessionKey) {
                    throw new Error('Node does not have a content key packet session key');
                }
                return keys.contentKeyPacketSessionKey;
            },
        };
    }
    /**
     * @returns The root folder to the public link.
     */
    async getRootNode() {
        this.logger.info(`Getting root node`);
        const { rootNodeUid } = await this.sharingPublic.shares.getOwnVolumeIDs();
        return (0, transformers_1.convertInternalNodePromise)(this.sharingPublic.nodes.access.getNode(rootNodeUid));
    }
    /**
     * Iterates the children of the given parent node.
     *
     * See `ProtonDriveClient.iterateFolderChildren` for more information.
     */
    async *iterateFolderChildren(parentUid, filterOptions, signal) {
        this.logger.info(`Iterating children of ${(0, transformers_1.getUid)(parentUid)}`);
        yield* (0, transformers_1.convertInternalNodeIterator)(this.sharingPublic.nodes.access.iterateFolderChildren((0, transformers_1.getUid)(parentUid), filterOptions, signal));
    }
    /**
     * Iterates the nodes by their UIDs.
     *
     * See `ProtonDriveClient.iterateNodes` for more information.
     */
    async *iterateNodes(nodeUids, signal) {
        this.logger.info(`Iterating ${nodeUids.length} nodes`);
        yield* (0, transformers_1.convertInternalMissingNodeIterator)(this.sharingPublic.nodes.access.iterateNodes((0, transformers_1.getUids)(nodeUids), signal));
    }
    /**
     * Get the node by its UID.
     *
     * See `ProtonDriveClient.getNode` for more information.
     */
    async getNode(nodeUid) {
        this.logger.info(`Getting node ${(0, transformers_1.getUid)(nodeUid)}`);
        return (0, transformers_1.convertInternalNodePromise)(this.sharingPublic.nodes.access.getNode((0, transformers_1.getUid)(nodeUid)));
    }
    /**
     * Get the file downloader to download the node content.
     *
     * See `ProtonDriveClient.getFileDownloader` for more information.
     */
    async getFileDownloader(nodeUid, signal) {
        this.logger.info(`Getting file downloader for ${(0, transformers_1.getUid)(nodeUid)}`);
        return this.download.getFileDownloader((0, transformers_1.getUid)(nodeUid), signal);
    }
    /**
     * Iterates the thumbnails of the given nodes.
     *
     * See `ProtonDriveClient.iterateThumbnails` for more information.
     */
    async *iterateThumbnails(nodeUids, thumbnailType, signal) {
        this.logger.info(`Iterating ${nodeUids.length} thumbnails`);
        yield* this.download.iterateThumbnails((0, transformers_1.getUids)(nodeUids), thumbnailType, signal);
    }
}
exports.ProtonDrivePublicLinkClient = ProtonDrivePublicLinkClient;
//# sourceMappingURL=protonDrivePublicLinkClient.js.map